import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';
import { CirclePlayIcon, CircleStopIcon, EllipsisVerticalIcon } from 'lucide-react';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { ReplayButton } from '@/components/atoms/ReplayButton/ReplayButton';
import { RowActionIndicator } from '@/components/atoms/RowActionIndicator/RowActionIndicator';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import { ChannelErrorTooltip } from '@/modules/campaigns/components/ChannelErrorTooltip';
import { getChannelIcon } from '@/modules/campaigns/components/Tables/CampaignsTable/ChannelCell';
import {
  getSubscriberStopInfo,
  isSubscriberResumable,
  shouldShowStoppedBadge,
} from '@/modules/campaigns/util/getSubscriberStopInfo';
import { getReplayability } from '@/modules/campaigns/utils';
import { CampaignStatus, CampaignSubscriber } from '@/shared/types/campaigns';
import { IntegrationType } from '@/shared/types/integration';
import { getChannelKey } from '@/util/channelName';
import { COLUMN_PRESETS } from '@/util/constants';
import { formatDate } from '@/util/dateUtils';

import { NextActionCell } from './NextActionCell';

const isStopped = (subscriber: CampaignSubscriber): boolean =>
  shouldShowStoppedBadge(getSubscriberStopInfo(subscriber));

interface ColumnsProps {
  lastSeen: string[];
  onReplay: (sessionId: string) => void;
  campaign_senders?: Array<{ sender_id: string; integration_type?: string }>;
  onStopClick?: (subscriberId: string) => void;
  onResumeClick?: (subscriberId: string) => void;
  campaignStatus?: CampaignStatus;
  /** Whether the campaign runs NBA. Resume only applies to agentic campaigns. */
  isAgentic?: boolean;
  hideReplay?: boolean;
}

const DATE_FORMAT = 'MMM d, HH:mm';

export const columns = ({
  lastSeen,
  onReplay,
  campaign_senders = [],
  onStopClick,
  onResumeClick,
  campaignStatus,
  isAgentic,
  hideReplay,
}: ColumnsProps): ColumnDef<CampaignSubscriber>[] => {
  const senderTypeMap = new Map(campaign_senders.map((s) => [s.sender_id, s.integration_type]));

  return [
    ...(hideReplay
      ? []
      : [
          {
            id: 'replay',
            enableHiding: false,
            header: () => null,
            cell: ({ row }: { row: { original: CampaignSubscriber } }) => {
              const { sessionId, canReplay } = getReplayability(row.original);

              return (
                <ReplayButton
                  sessionId={sessionId}
                  lastSeen={lastSeen}
                  onClick={sessionId ? () => onReplay(sessionId) : undefined}
                  disabled={!canReplay}
                />
              );
            },
            size: 72,
            minSize: 72,
            maxSize: 72,
          } satisfies ColumnDef<CampaignSubscriber>,
        ]),
    {
      id: 'email',
      accessorKey: 'email',
      header: ({ column }) => <SortableHeader column={column}>{t('common.email')}</SortableHeader>,
      cell: ({ row }) => {
        const email = row.getValue('email') as string;
        return (
          <div className="flex items-center gap-2">
            <span className="min-w-0 truncate font-medium">{email || '-'}</span>
            {hideReplay && <RowActionIndicator action="view" />}
          </div>
        );
      },
      ...COLUMN_PRESETS.FLEX,
      // Override FLEX minSize (384) to prevent horizontal scroll within the page layout container
      minSize: 200,
      meta: { preset: 'FLEX', label: 'common.email' },
    },
    {
      id: 'phone',
      accessorKey: 'phone',
      header: ({ column }) => <SortableHeader column={column}>{t('common.phone')}</SortableHeader>,
      cell: ({ row }) => {
        const phone = row.getValue('phone') as string;
        return <div className="font-medium">{phone || '-'}</div>;
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'common.phone' },
    },
    ...(isAgentic
      ? [
          {
            id: 'next_action',
            enableSorting: false,
            header: () => t('campaigns.subscriber_journey.next_action.column'),
            cell: ({ row }: { row: { original: CampaignSubscriber } }) => (
              <NextActionCell
                nextAction={row.original.next_action}
                timeZone={row.original.context?.time_zone}
              />
            ),
            ...COLUMN_PRESETS.LG,
            meta: { preset: 'LG', label: 'campaigns.subscriber_journey.next_action.column' },
          } satisfies ColumnDef<CampaignSubscriber>,
        ]
      : []),
    {
      id: 'channels',
      enableSorting: false,
      header: () => t('common.channels'),
      cell: ({ row }) => {
        const senderHistory = row.original.sender_history;
        if (!senderHistory?.length) {
          return '-';
        }

        const uniqueTypes = [
          ...new Set(
            senderHistory
              .map((entry) => senderTypeMap.get(entry.sender_id))
              .filter((type): type is string => !!type)
          ),
        ];

        return (
          <div className="flex items-center gap-1.5">
            {uniqueTypes.map((type) => (
              <InfoTooltip
                key={type}
                content={t(getChannelKey(type as IntegrationType))}
                side="top"
              >
                <span>{getChannelIcon(type)}</span>
              </InfoTooltip>
            ))}
          </div>
        );
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'common.channels' },
    },
    {
      id: 'sent',
      accessorKey: 'last_sent_at',
      enableHiding: false,
      header: ({ column }) => (
        <SortableHeader column={column}>{t('broadcasts.recipients_status.sent')}</SortableHeader>
      ),
      cell: ({ row }) => {
        const subscriber = row.original;
        const failedAt = subscriber.last_failed_at;
        const sentAt = subscriber.last_sent_at;

        if (failedAt) {
          const senderId = subscriber.sender_history?.[0]?.sender_id;
          const statusCode = subscriber.sender_history?.[0]?.status_code;

          const integrationType = senderTypeMap.get(senderId) as IntegrationType | undefined;

          return (
            <ChannelErrorTooltip channel={integrationType} status_code={statusCode}>
              <Badge type="secondary" flavour="destructive" rounded={false}>
                {t('status.failed')}
              </Badge>
            </ChannelErrorTooltip>
          );
        } else if (sentAt) {
          return formatDate(sentAt, DATE_FORMAT);
        } else {
          return '-';
        }
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD' },
    },
    {
      id: 'delivered',
      accessorKey: 'last_delivered_at',
      header: ({ column }) => (
        <SortableHeader column={column}>
          {t('broadcasts.recipients_status.delivered')}
        </SortableHeader>
      ),
      cell: ({ row }) => {
        const { last_delivered_at, last_sent_at, last_failed_at } = row.original;
        if (last_delivered_at) {
          return formatDate(last_delivered_at, DATE_FORMAT);
        } else if (last_sent_at && !last_failed_at) {
          return (
            <Badge type="secondary" flavour="informational" rounded={false}>
              {t('status.pending')}
            </Badge>
          );
        } else {
          return '-';
        }
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'broadcasts.recipients_status.delivered' },
    },
    {
      id: 'read',
      accessorKey: 'last_read_at',
      header: ({ column }) => (
        <SortableHeader column={column}>{t('broadcasts.recipients_status.read')}</SortableHeader>
      ),
      cell: ({ row }) => {
        const { last_read_at, last_delivered_at } = row.original;
        if (last_read_at) {
          return formatDate(last_read_at, DATE_FORMAT);
        } else if (last_delivered_at) {
          return (
            <Badge type="secondary" flavour="informational" rounded={false}>
              {t('status.pending')}
            </Badge>
          );
        } else {
          return '-';
        }
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'broadcasts.recipients_status.read' },
    },
    {
      id: 'replied',
      accessorKey: 'last_replied_at',
      header: ({ column }) => (
        <SortableHeader column={column}>{t('broadcasts.recipients_status.replied')}</SortableHeader>
      ),
      cell: ({ row }) => {
        const { last_replied_at, last_read_at } = row.original;
        if (last_replied_at) {
          return formatDate(last_replied_at, DATE_FORMAT);
        } else if (last_read_at) {
          return (
            <Badge type="secondary" flavour="informational" rounded={false}>
              {t('status.pending')}
            </Badge>
          );
        } else {
          return '-';
        }
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'broadcasts.recipients_status.replied' },
    },
    {
      id: 'status',
      enableSorting: false,
      header: () => t('common.status'),
      cell: ({ row }) =>
        isStopped(row.original) ? (
          <Badge type="secondary" flavour="neutral" rounded={false}>
            {t('status.stopped')}
          </Badge>
        ) : (
          <Badge type="secondary" flavour="successful" rounded={false}>
            {t('status.active')}
          </Badge>
        ),
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'common.status' },
    },
    ...(campaignStatus === 'in_progress'
      ? [
          {
            id: 'actions',
            enableHiding: false,
            header: () => null,
            cell: ({ row }: { row: { original: CampaignSubscriber } }) => {
              const subscriber = row.original;
              const stopInfo = getSubscriberStopInfo(subscriber);
              const stopped = shouldShowStoppedBadge(stopInfo);
              // Resume only on agentic campaigns (backend rejects otherwise); else Stop if active.
              const showResume = stopped && isSubscriberResumable(stopInfo) && !!isAgentic;
              const showStop = !stopped;

              if (!showResume && !showStop) {
                return null;
              }

              return (
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      variant="ghost-neutral"
                      size="icon-sm"
                    >
                      <EllipsisVerticalIcon size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {showResume ? (
                      <DropdownMenuItem
                        onSelect={() => onResumeClick?.(subscriber.subscriber_id)}
                        className="flex items-center gap-2"
                      >
                        <CirclePlayIcon size={16} className="text-foreground-muted" />
                        {`${t('campaigns.resume_subscriber')}…`}
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        onSelect={() => onStopClick?.(subscriber.subscriber_id)}
                        className="flex items-center gap-2"
                      >
                        <CircleStopIcon size={16} className="text-foreground-muted" />
                        {`${t('campaigns.stop_subscriber')}…`}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            },
            ...COLUMN_PRESETS.SM,
            meta: { preset: 'SM' },
          } satisfies ColumnDef<CampaignSubscriber>,
        ]
      : []),
  ];
};
